import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

export type MenuType = 'default' | 'compact' | 'icon';
export type TopbarTheme = 'light' | 'dark';
export type LayoutMode = 'vertical' | 'horizontal';

interface LayoutPreferences {
    layoutMode: LayoutMode;
    topbarTheme: TopbarTheme;
    sidebarType: MenuType;
    sidebarColor: string;
}

interface LayoutPreferencesContextType extends LayoutPreferences {
    setLayoutMode: (mode: LayoutMode) => void;
    setTopbarTheme: (theme: TopbarTheme) => void;
    setSidebarType: (type: MenuType) => void;
    setSidebarColor: (color: string) => void;
}

const defaultPreferences: LayoutPreferences = {
    layoutMode: 'vertical',
    topbarTheme: 'light',
    sidebarType: 'default',
    sidebarColor: '#556ee6'
};

const LayoutPreferencesContext = createContext<LayoutPreferencesContextType | undefined>(undefined);

export const useLayoutPreferences = () => {
    const context = useContext(LayoutPreferencesContext);
    if (!context) {
        throw new Error('useLayoutPreferences must be used within a LayoutPreferencesProvider');
    }
    return context;
};

interface LayoutPreferencesProviderProps {
    children: ReactNode;
}

export const LayoutPreferencesProvider = ({ children }: LayoutPreferencesProviderProps) => {
    const [preferences, setPreferences] = useState<LayoutPreferences>(() => {
        const savedPreferences = localStorage.getItem('layoutPreferences');
        if (savedPreferences) {
            return JSON.parse(savedPreferences);
        }
        // Fallback : lire les anciennes clés individuelles (legacy)
        return {
            layoutMode: (localStorage.getItem('layoutMode') as LayoutMode) || 'vertical',
            topbarTheme: (localStorage.getItem('headerTheme') as TopbarTheme) || 'light',
            sidebarType: (localStorage.getItem('sidebarType') as MenuType) || 'default',
            sidebarColor: localStorage.getItem('sidebarColor') || '#556ee6',
        };
    });

    const updatePreferences = useCallback((updates: Partial<LayoutPreferences>) => {
        setPreferences(prev => {
            const next = { ...prev, ...updates };
            localStorage.setItem('layoutPreferences', JSON.stringify(next));
            return next;
        });
    }, []);

    const setLayoutMode = useCallback((mode: LayoutMode) => {
        updatePreferences({ layoutMode: mode });
    }, [updatePreferences]);

    const setTopbarTheme = useCallback((theme: TopbarTheme) => {
        updatePreferences({ topbarTheme: theme });
    }, [updatePreferences]);

    const setSidebarType = useCallback((type: MenuType) => {
        updatePreferences({ sidebarType: type });
    }, [updatePreferences]);

    const setSidebarColor = useCallback((color: string) => {
        updatePreferences({ sidebarColor: color });
    }, [updatePreferences]);

    const value: LayoutPreferencesContextType = useMemo(() => ({
        ...preferences,
        setLayoutMode,
        setTopbarTheme,
        setSidebarType,
        setSidebarColor,
    }), [preferences, setLayoutMode, setTopbarTheme, setSidebarType, setSidebarColor]);

    return (
        <LayoutPreferencesContext.Provider value={value}>
            {children}
        </LayoutPreferencesContext.Provider>
    );
};
